import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Product } from '@/types/product';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { FEATURED_PRODUCTS } from '@/data/products';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowLeft, Heart, ShoppingCart, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FloatingChat } from '@/components/FloatingChat';
import { BackToTop } from '@/components/BackToTop';
import { useCartStore } from '@/store/cartStore';
import { useFavoritesStore } from '@/store/favoritesStore';
import { toast } from 'sonner';
import { ProductGrid } from '@/components/ProductGrid';
import { SortOption } from '@/components/search/searchUtils';
import { OptimizedImage } from '@/components/OptimizedImage';
import { Skeleton } from '@/components/ui/skeleton';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { addItem } = useCartStore();
  const { isFavorite, addFavorite, removeFavorite } = useFavoritesStore();
  
  // Add these state variables for Header props
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("name-asc");
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  
  // Memoize related products calculation
  const relatedProducts = useMemo(() => {
    if (!product) return [];
    
    // Find related products in the same category
    return FEATURED_PRODUCTS
      .filter(p => p.category === product.category && p.id !== product.id)
      .slice(0, 4);
  }, [product]);
  
  useEffect(() => {
    // In a real app, this would be an API call
    setIsLoading(true);
    
    // Simulate API call
    const fetchProductDetail = setTimeout(() => {
      const foundProduct = FEATURED_PRODUCTS.find(p => p.id === id);
      setProduct(foundProduct || null);
      setIsLoading(false);
    }, 800);
    
    // Scroll to top when product changes
    window.scrollTo(0, 0);
    
    return () => clearTimeout(fetchProductDetail);
  }, [id]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header 
          showSearchInHeader={false}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          sortBy={sortBy}
          setSortBy={setSortBy}
          filterMenuOpen={filterMenuOpen}
          setFilterMenuOpen={setFilterMenuOpen}
          sortMenuOpen={sortMenuOpen}
          setSortMenuOpen={setSortMenuOpen}
        />
        <main className="flex-1 container mx-auto px-4 py-8">
          <Link to="/" className="flex items-center mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to products
          </Link>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Skeleton className="aspect-square rounded-lg" />
            <div>
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-64 mb-6" />
              <div className="flex gap-1 mb-4">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-4" />
              </div>
              <Skeleton className="h-8 w-32 mb-6" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4 mb-6" />
              <div className="flex gap-4 mt-8">
                <Skeleton className="h-12 flex-1 rounded-full" />
                <Skeleton className="h-12 w-12 rounded-full" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header 
          showSearchInHeader={false}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          sortBy={sortBy}
          setSortBy={setSortBy}
          filterMenuOpen={filterMenuOpen}
          setFilterMenuOpen={setFilterMenuOpen}
          sortMenuOpen={sortMenuOpen}
          setSortMenuOpen={setSortMenuOpen}
        />
        <main className="flex-1 container mx-auto px-4 py-8">
          <Link to="/" className="flex items-center mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to products
          </Link>
          <div className="text-center py-16">
            <h1 className="text-2xl font-bold mb-4">Product Not Found</h1>
            <p className="mb-8">The product you're looking for doesn't exist or has been removed.</p>
            <Button asChild>
              <Link to="/">Return to Homepage</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  const favorite = isFavorite(product.id);
  
  const handleAddToCart = () => {
    if (product.prescription) {
      toast.error("This medication requires a prescription. Please upload one before purchasing.");
      return;
    }

    addItem({
      id: product.id,
      title: product.title,
      price: product.price,
      image: product.image,
      category: product.category,
      prescription: product.prescription,
    });
    
    toast.success(`${product.title} has been added to your medication tray`);
  };

  const handleToggleFavorite = () => {
    if (favorite) {
      removeFavorite(product.id);
      toast.success(`${product.title} has been removed from your favorites`);
    } else {
      addFavorite(product);
      toast.success(`${product.title} has been added to your favorites`);
    }
  };
  
  // Simulated product details
  const getProductDescription = () => {
    const descriptions: Record<string, string> = {
      "Pain Relief": "This medication provides fast and effective relief from pain and inflammation. It works by blocking the production of certain natural substances that cause inflammation, thereby reducing pain and swelling.",
      "Antibiotics": "This antibiotic belongs to a class of drugs known as macrolides. It works by stopping the growth of bacteria. This medication will not work for viral infections (such as common cold, flu).",
      "Diabetes": "This medication helps control blood sugar levels in people with type 2 diabetes. It works by decreasing the amount of sugar your liver makes and helping your body respond better to the insulin it produces naturally.",
      "Heart Health": "This medication is used to treat high blood pressure (hypertension) and chest pain (angina). It works by relaxing blood vessels so blood can flow more easily.",
      "Vitamins": "This supplement provides essential nutrients that may be missing from your diet. It helps maintain overall health and wellbeing by supporting various bodily functions.",
      "Respiratory": "This medication is used to prevent or treat wheezing, difficulty breathing, chest tightness, and coughing caused by certain breathing problems. It works by relaxing muscles in the airways to improve breathing."
    };
    
    return descriptions[product.category] || "No detailed description available for this product.";
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header 
        showSearchInHeader={false}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filterMenuOpen={filterMenuOpen}
        setFilterMenuOpen={setFilterMenuOpen}
        sortMenuOpen={sortMenuOpen}
        setSortMenuOpen={setSortMenuOpen}
      />
      
      <main className="flex-1">
        <div className="container mx-auto px-4 py-8">
          <Link to="/" className="flex items-center mb-8 hover:underline transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to products
          </Link>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
              <OptimizedImage 
                src={product.image} 
                alt={product.title} 
                className="w-full h-full object-contain aspect-square"
              />
            </div>
            
            <div>
              <div className="mb-6">
                <div className="flex flex-wrap gap-2 mb-2">
                  <Badge variant="outline">{product.category}</Badge>
                  {product.prescription && (
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                      <AlertCircle className="mr-1 h-3 w-3" />
                      Prescription Required
                    </Badge>
                  )}
                </div>
                
                <h1 className="text-3xl font-bold">{product.title}</h1>
                
                <div className="flex items-center mt-2 mb-4">
                  <div className="flex mr-2">
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    <Star className="h-5 w-5 text-muted-foreground/30" />
                  </div>
                  <span className="text-sm text-muted-foreground">4.0 (24 reviews)</span>
                </div>
                
                <p className="text-3xl font-semibold mb-4">₦{product.price.toLocaleString()}</p>
                
                <p className="text-muted-foreground mb-6">{getProductDescription()}</p>
                
                <div className="flex flex-wrap gap-4 mt-8">
                  <Button 
                    size="lg" 
                    className="flex-1 rounded-full hover:scale-[1.01] transition-transform" 
                    onClick={handleAddToCart}
                  >
                    <ShoppingCart className="mr-2 h-5 w-5" />
                    {product.prescription ? "Upload Prescription" : "Add to Tray"}
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="w-12 h-12 p-0 rounded-full hover:scale-105 transition-all"
                    onClick={handleToggleFavorite}
                  >
                    <Heart className={`h-5 w-5 ${favorite ? 'fill-red-500 text-red-500' : ''}`} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <Tabs defaultValue="details" className="mb-12">
            <TabsList className="w-full border-b rounded-none justify-start mb-6">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="side-effects">Side Effects</TabsTrigger>
              <TabsTrigger value="usage">Usage</TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="prose dark:prose-invert max-w-none">
              <h3>Product Details</h3>
              <p>{getProductDescription()}</p>
              <ul>
                <li>Category: {product.category}</li>
                <li>SKU: {product.id}</li>
                <li>{product.prescription ? "Prescription required" : "No prescription needed"}</li>
                <li>In stock and ready to ship</li>
              </ul>
            </TabsContent>
            
            <TabsContent value="side-effects" className="prose dark:prose-invert max-w-none">
              <h3>Possible Side Effects</h3>
              <p>
                {product.prescription 
                  ? "Common side effects may include nausea, drowsiness, dizziness, headache, dry mouth, or mild stomach pain. Serious side effects are rare when taken as directed. Contact your healthcare provider if you experience any severe or persistent side effects." 
                  : "When used as directed, this product has minimal side effects. Some people may experience mild stomach upset or allergic reactions. Discontinue use and consult a healthcare professional if any adverse reactions occur."}
              </p>
              <p className="text-muted-foreground italic mt-4">
                This information is not comprehensive. Always read the package insert and consult with a healthcare professional.
              </p>
            </TabsContent>
            
            <TabsContent value="usage" className="prose dark:prose-invert max-w-none">
              <h3>Usage Instructions</h3>
              <p>
                {product.prescription
                  ? "Take this medication exactly as prescribed by your doctor. Do not take in larger or smaller amounts or for longer than recommended. Follow all directions on your prescription label. If you have any questions, ask your doctor or pharmacist."
                  : "For adults, take as needed according to the package instructions. Do not exceed the recommended dose. If symptoms persist for more than a few days, consult with a healthcare professional."}
              </p>
              <p className="text-muted-foreground italic mt-4">
                Always consult with a healthcare professional for proper dosage and administration.
              </p>
            </TabsContent>
          </Tabs>
          
          {relatedProducts.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Related Products</h2>
              <ProductGrid 
                products={relatedProducts} 
                isLoading={false} 
              />
            </div>
          )}
        </div>
      </main>
      
      <BackToTop />
      <FloatingChat />
      <Footer />
    </div>
  );
}
